// src/components/alerts/AlertPagination.js
// --- UPDATE (Alert System AI) — NEW: the page's pagination, extracted, plus a page-size picker.
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Flex, HStack, Icon, IconButton, Select, Text, useColorModeValue } from '@chakra-ui/react';
import { MdArrowBack, MdArrowForward, MdFirstPage, MdLastPage } from 'react-icons/md';

const pageNumbers = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [1];
    if (current > 3) pages.push('…');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('…');
    pages.push(total);
    return pages;
};

function AlertPagination({ pagination, onPageChange, onLimitChange, isLoading, pageSizes }) {
    const muted = useColorModeValue('gray.500', 'gray.400');
    if (!pagination) return null;
    const { currentPage = 1, totalPages = 1, totalItems = 0, limit = 10 } = pagination;
    const start = totalItems ? (currentPage - 1) * limit + 1 : 0;
    const end = Math.min(currentPage * limit, totalItems);
    return (
        <Flex mt={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <Text fontSize="sm" color={muted}>{totalItems ? `${start}–${end} of ${totalItems}` : 'No items'}</Text>
            {totalPages > 1 && (
                <HStack spacing={1}>
                    <IconButton size="sm" aria-label="First page" icon={<Icon as={MdFirstPage} />} onClick={() => onPageChange(1)} isDisabled={currentPage <= 1 || isLoading} variant="ghost" />
                    <IconButton size="sm" aria-label="Previous page" icon={<Icon as={MdArrowBack} />} onClick={() => onPageChange(currentPage - 1)} isDisabled={currentPage <= 1 || isLoading} variant="ghost" />
                    {pageNumbers(currentPage, totalPages).map((p, i) => (p === '…'
                        ? <Text key={`e${i}`} px={1} color={muted}>…</Text>
                        : <Button key={p} size="sm" minW="36px" variant={p === currentPage ? 'solid' : 'ghost'} colorScheme={p === currentPage ? 'blue' : 'gray'}
                            onClick={() => onPageChange(p)} isDisabled={isLoading} aria-current={p === currentPage ? 'page' : undefined}>{p}</Button>))}
                    <IconButton size="sm" aria-label="Next page" icon={<Icon as={MdArrowForward} />} onClick={() => onPageChange(currentPage + 1)} isDisabled={currentPage >= totalPages || isLoading} variant="ghost" />
                    <IconButton size="sm" aria-label="Last page" icon={<Icon as={MdLastPage} />} onClick={() => onPageChange(totalPages)} isDisabled={currentPage >= totalPages || isLoading} variant="ghost" />
                </HStack>
            )}
            {onLimitChange && (
                <HStack>
                    <Text fontSize="sm" color={muted}>Rows</Text>
                    <Select size="sm" w="80px" value={limit} onChange={e => onLimitChange(parseInt(e.target.value, 10))} borderRadius="md" aria-label="Rows per page">
                        {pageSizes.map(n => <option key={n} value={n}>{n}</option>)}
                    </Select>
                </HStack>
            )}
        </Flex>
    );
}

AlertPagination.propTypes = {
    pagination: PropTypes.object,
    onPageChange: PropTypes.func.isRequired,
    onLimitChange: PropTypes.func,
    isLoading: PropTypes.bool,
    pageSizes: PropTypes.arrayOf(PropTypes.number),
};
AlertPagination.defaultProps = { pageSizes: [10, 25, 50, 100] };

export default React.memo(AlertPagination);
// --- END UPDATE
